import { Story } from '../../types/story';

export const edinburghReunion: Story = {
  id: 'edinburgh-reunion',
  title: 'The Reunion',
  excerpt: 'Ten years after a devastating breakup, former lovers collide at a wedding in Edinburgh and discover that some flames never truly die.',
  city: 'Edinburgh',
  publishDate: '2025-12-02',
  readTime: '29 min read',
  tags: ['♀♂', 'ex-lovers', 'wedding', 'emotional'],
  categories: ['forbidden', 'romantic', 'intense'],
  intensity: 3,
  content: `I saw her name on the seating chart and my stomach dropped. Sophie Mitchell. Table 7. Two seats away from mine.

Ten years. It had been ten years since I'd last seen her, since our relationship had imploded in a spectacular mess of accusations and slammed doors and things we could never take back. And now we were both going to be at our mutual friend's wedding in Edinburgh, pretending we were mature adults who could handle being in the same room.

I considered not going. Made up excuses in my head—work emergency, sudden illness, family crisis. But Marcus was one of my oldest friends, and missing his wedding because of ancient history would have been cowardly. So I got on the train from London to Edinburgh, checked into the hotel, and tried to prepare myself for the inevitable awkwardness.

The rehearsal dinner was Friday night at a restaurant in the Old Town. I arrived late on purpose, hoping to slip in unnoticed. But the moment I walked through the door, I saw her.

She was standing by the bar, laughing at something someone said, and she looked exactly the same and completely different all at once. Same dark hair, now cut shorter. Same green eyes that had always seen right through me. But there was something else—a confidence, a polish she hadn't had at twenty-three. She was wearing a simple black dress and a diamond ring on her left hand that caught the light.

She was engaged.

Of course she was. Why wouldn't she be? It had been a decade. People moved on. I'd been married and divorced in that time. But seeing that ring felt like a punch to the gut.

She saw me at the same moment I saw her. Our eyes met across the room, and for a second, the entire restaurant disappeared. It was just us, frozen in that moment of recognition and all the history that came with it.

Then Marcus appeared, pulling me into a hug, and the spell broke.

"James! You made it! Come on, let me introduce you to everyone."

He dragged me around the room, introducing me to relatives and friends, and I smiled and made small talk and tried not to look at Sophie. But I could feel her watching me. Could feel the weight of her presence like a physical thing.

We didn't speak that night. Managed to avoid each other through dinner and drinks and the various wedding-related activities. But the tension was there, crackling between us like electricity.

Saturday was the wedding. The ceremony was at St. Giles' Cathedral, all Gothic architecture and stained glass. I sat in the back, watching Marcus and his bride exchange vows, and tried not to think about the fact that Sophie was sitting three rows ahead of me, that diamond ring glinting on her finger.

The reception was at a hotel in the New Town, a beautiful Georgian building with high ceilings and chandeliers. I found my seat at Table 7 and discovered that Sophie was directly across from me. Of course she was.

"Hello, James." Her voice was exactly as I remembered—low, slightly husky, the kind of voice that had always made me want to lean in closer.

"Sophie. It's been a while."

"Ten years."

"Give or take."

We sat in awkward silence as the other guests arrived and took their seats. The couple next to me—Marcus's cousins—tried to make conversation, but I could barely focus. All I could think about was Sophie sitting across from me, so close I could smell her perfume, the same one she'd worn in university.

Dinner was torture. Every time I looked up, she was there. Every time I reached for my wine glass, our eyes met. The conversation at the table flowed around us, but we barely participated, too aware of each other to focus on anything else.

After dinner, people started dancing. I escaped to the bar, ordered a whisky, and tried to get my head together. This was ridiculous. We were adults. We'd both moved on. There was no reason for this to be so difficult.

"Can I have one of those?" Sophie appeared beside me, gesturing to my drink.

"Sure." I ordered her a whisky. The bartender poured it, and she took a sip, grimacing slightly.

"Still too strong for me. Some things don't change."

"Some things do. You're engaged."

She looked down at her ring. "Yes. His name is David. He's a lawyer. Very stable. Very sensible. Everything you weren't."

"Sounds perfect."

"It is. It's exactly what I should want."

"Should?"

She didn't answer. Instead, she finished her drink in one go and set the glass down hard. "Do you want to get out of here? Just for a bit. I need air."

We walked through the streets of Edinburgh, neither of us speaking. The city was beautiful at night, all lit buildings and cobblestone streets. We ended up at Calton Hill, looking out over the city, the castle lit up in the distance.

"Why did you come?" she asked finally. "To the wedding. You could have made an excuse."

"Marcus is my friend. Why did you come?"

"Same reason. And because I thought I was over you. Thought I could handle seeing you again."

"And can you?"

She turned to face me, and in the moonlight, she looked exactly like she had at twenty-three, standing in my flat, telling me she couldn't do this anymore, that we were too intense, too volatile, that we'd destroy each other if we stayed together.

"No," she said quietly. "I can't. Because the moment I saw you, everything came back. All of it. The good and the bad and the absolutely terrible."

"Sophie—"

"Do you remember the last thing you said to me? When I left?"

I did. I'd said a lot of things that night, most of them cruel, designed to hurt her the way she was hurting me. But the last thing, the thing I'd shouted as she walked out the door, was: "You'll regret this. You'll spend the rest of your life wondering what if."

"I remember."

"You were right. I have wondered. Every relationship since then, I've compared to what we had. And nothing's ever measured up. Not even David, who's perfect on paper. Because he doesn't make me feel the way you did. Doesn't make me feel anything, really."

"Then why are you marrying him?"

"Because what we had was unsustainable. We fought constantly. We were jealous and possessive and we brought out the worst in each other. It was passionate, yes, but it was also toxic. I can't live like that."

"So you'd rather live with someone you don't love?"

"I'd rather live with someone who doesn't make me crazy. Who doesn't make me question everything. Who lets me be calm."

"That sounds boring."

"It is. But it's safe."

We stood there in silence, the weight of ten years between us. I wanted to tell her she was making a mistake, that safe was overrated, that what we'd had was worth fighting for. But I also knew she was right. We'd been terrible for each other. Passionate, yes, but also destructive.

"I was married," I said finally. "For three years. It ended last year."

"I know. Marcus told me. I'm sorry."

"Don't be. It was the right thing. She was nice. We were compatible. But I didn't love her. Not the way I—" I stopped myself.

"Not the way you what?"

"Not the way I loved you."

She closed her eyes, and when she opened them, they were wet. "Don't say that."

"Why not? It's true. I've spent ten years trying to get over you, Sophie. Tried to convince myself that what we had wasn't real, that it was just intense because we were young. But seeing you tonight, I know that's bullshit. I never got over you. I just learned to live with the fact that you were gone."

"James, I'm engaged. I'm getting married in three months."

"I know."

"So what are we doing here? What's the point of this conversation?"

"I don't know. Maybe there isn't one. Maybe we're just torturing ourselves."

She laughed, but it came out as a sob. "We're good at that."

I stepped closer, and she didn't move away. We were inches apart now, close enough that I could see the tears on her cheeks, could feel her breath.

"Tell me you don't feel it," I said. "Tell me you don't feel exactly what I'm feeling right now, and I'll walk away. I'll go back to the hotel and we'll never speak again."

"I can't."

"Then what do you want to do about it?"

She looked at me for a long moment, and I saw the war playing out on her face—duty versus desire, safety versus passion, the life she'd planned versus the one she'd left behind.

"I want to make a terrible decision," she said finally. "Just for tonight. One last time."

We took a taxi back to my hotel. Didn't speak during the ride, just sat with our hands almost touching, the tension so thick it was hard to breathe. In the elevator, we stood on opposite sides, watching the numbers climb, both of us knowing this was a mistake and neither of us caring.

My room was on the fifth floor. I fumbled with the key card, my hands shaking, and finally got the door open. We went inside, and the moment the door closed behind us, we were on each other.

The kiss was desperate, urgent, ten years of longing compressed into a single moment. She tasted like whisky and regret, and I couldn't get enough. We stumbled toward the bed, shedding clothes as we went—her dress, my jacket, her shoes, my shirt.

"We shouldn't be doing this," she gasped between kisses.

"I know."

"I'm engaged."

"I know."

"This is wrong."

"I know."

But neither of us stopped. We fell onto the bed, and I took a moment just to look at her—her body, her face, the way she was looking at me like I was the only thing in the world that mattered.

"God, I've missed you," I said.

"Shut up and kiss me."

I did. Kissed her mouth, her neck, her breasts. Relearned her body like it was a map I'd once memorized but had forgotten the details of. She was the same and different—a scar on her hip that hadn't been there before, a tattoo on her ribs, small changes that marked the passage of time.

When I entered her, we both gasped. It felt like coming home and jumping off a cliff at the same time—familiar and terrifying and absolutely right.

"Fuck," she moaned. "I forgot how good this was."

"I didn't. I remembered every second."

We moved together, finding the rhythm we'd had a decade ago, like our bodies had never forgotten even if our minds had tried to. It was intense and emotional and raw—not just physical but something deeper, something that had nothing to do with technique and everything to do with history.

"Look at me," I said, and she did. Her eyes were wet, and I realized I was crying too. "I love you. I never stopped."

"Don't," she sobbed. "Please don't."

"I have to. Because this is it, isn't it? This is the last time. So I need you to know—I love you. I've always loved you. And I'm sorry. For everything I said, everything I did. I'm sorry we couldn't make it work."

She pulled me down, kissing me hard, and I felt her tears mixing with mine. We made love like it was the end of the world, desperate and passionate and heartbreaking. When we came, we came together, crying out each other's names, holding on like we could stop time if we just held tight enough.

Afterward, we lay tangled together, both of us crying, both of us knowing this changed nothing and everything.

"What do we do now?" she asked.

"I don't know."

"I can't leave him. The wedding's in three months. Everything's planned. Our families, our friends—everyone's expecting it."

"I know."

"But I can't stop thinking about you. About this. About what we could have been if we'd been different people."

"We were who we were. Young and stupid and too intense for our own good."

"Are we different now?"

I thought about it. "I don't know. Maybe. I'm not as angry as I used to be. Not as jealous. I've learned to be alone without falling apart."

"I've learned to be calm. To not need constant drama and passion. To be content with good enough."

"Is that really what you want? Good enough?"

She didn't answer. Instead, she got up and started getting dressed. I watched her, memorizing every movement, knowing this was the last time I'd see her like this.

"I should go," she said. "It's late. People will notice I'm gone."

"Sophie—"

"Don't. Please. If you say anything else, I'll fall apart, and I can't do that. I have to go back to the hotel and put on my dress and smile at the brunch tomorrow and pretend this didn't happen."

"And then what? You marry him? Spend the rest of your life wondering what if?"

"Yes. Because the alternative is blowing up my entire life for a relationship that didn't work the first time. And I can't do that. I'm not that brave."

She finished dressing and walked to the door. Paused with her hand on the handle.

"For what it's worth," she said without turning around, "I love you too. I never stopped either. But sometimes love isn't enough."

Then she was gone, and I was alone in the hotel room, surrounded by the ghost of what we'd been and what we'd never be again.

I didn't see her at the brunch the next morning. Someone said she'd left early, had to get back to London for work. I knew it was a lie, knew she'd left because she couldn't face me, couldn't pretend everything was fine.

I flew home that afternoon and went back to my life. Weeks passed, then months. I heard through Marcus that Sophie had gotten married, that the wedding had been beautiful, that she and David were very happy.

I tried to be happy for her. Tried to move on. But sometimes, late at night, I think about that night in Edinburgh. About the way she looked at me, the way she said my name, the way we fit together like we'd never been apart.

And I wonder if she thinks about it too. If she lies awake next to her husband and remembers what it felt like to be consumed by something bigger than yourself. If she regrets playing it safe.

I'll never know. And maybe that's for the best. Maybe some stories don't get happy endings. Maybe some people are meant to be the great love of your life but not the person you end up with.

All I know is that for one night in Edinburgh, we had each other again. And it was beautiful and terrible and everything in between.

And sometimes, that has to be enough.`
};
